import type { GeoPoint } from "./ukGeo";

export type MapTarget = {
  title?: string | null;
  location?: string | null;
  coordinates?: GeoPoint | null;
};

const normalizeText = (value: string | null | undefined) => value?.trim() ?? '';

const buildCoordinateQuery = (coordinates: GeoPoint | null | undefined) =>
  coordinates ? `${coordinates.latitude},${coordinates.longitude}` : '';

const buildLocationQuery = (target: MapTarget) => {
  const coordinateQuery = buildCoordinateQuery(target.coordinates);
  if (coordinateQuery) {
    return coordinateQuery;
  }

  const location = normalizeText(target.location);
  if (location) {
    return location;
  }

  return normalizeText(target.title);
};

const buildLocationLabel = (target: MapTarget) => normalizeText(target.title) || normalizeText(target.location);

export const hasMapTarget = (target: MapTarget) => Boolean(buildLocationQuery(target));

export const buildGoogleMapsSearchUrl = (target: MapTarget) => {
  const query = buildLocationQuery(target);
  if (!query) {
    return null;
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
};

export const buildGoogleMapsDirectionsUrl = (target: MapTarget) => {
  const destination = buildLocationQuery(target);
  if (!destination) {
    return null;
  }

  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(destination)}`;
};

export const buildGoogleMapsAppSearchUrl = (target: MapTarget) => {
  const query = buildLocationQuery(target);
  if (!query) {
    return null;
  }

  return `comgooglemaps://?q=${encodeURIComponent(query)}`;
};

export const buildGoogleMapsAppDirectionsUrl = (target: MapTarget) => {
  const destination = buildLocationQuery(target);
  if (!destination) {
    return null;
  }

  return `comgooglemaps://?daddr=${encodeURIComponent(destination)}&directionsmode=driving`;
};

export const buildAppleMapsSearchUrl = (target: MapTarget) => {
  const query = buildLocationQuery(target);
  if (!query) {
    return null;
  }

  const label = buildLocationLabel(target);
  if (target.coordinates) {
    const params = new URLSearchParams({
      ll: query,
    });

    if (label) {
      params.set('q', label);
    }

    return `http://maps.apple.com/?${params.toString()}`;
  }

  return `http://maps.apple.com/?q=${encodeURIComponent(query)}`;
};

export const buildAppleMapsDirectionsUrl = (target: MapTarget) => {
  const destination = buildLocationQuery(target);
  if (!destination) {
    return null;
  }

  return `http://maps.apple.com/?daddr=${encodeURIComponent(destination)}`;
};
